import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Currency, PurchaseItem } from '@galaxy/billing';
import { MatRipple } from '@angular/material/core';
import moment from 'moment';
import { PurchaseItemRow } from '../interface';
import { formatDisplayPrice } from '@vendasta/shared';

@Component({
  selector: 'billing-ui-purchase-item-list',
  templateUrl: './purchase-item-list.component.html',
  styleUrls: ['./purchase-item-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PurchaseItemListComponent implements OnInit {
  // can be 'name', 'sku', 'account', 'day', 'amount', 'discount', 'setupFee', 'total'
  @Input() displayedColumns: string[];
  @Input() purchaseItems: PurchaseItemRow[];
  @Input() loading = true;
  @Input() currency: Currency;
  @Input() showLinkToAccount: boolean;

  private _hasMore = true; // Default value since the parent component will send undefined initially
  @Input()
  set hasMore(value: boolean | undefined) {
    this._hasMore = value !== undefined ? value : true;
  }

  get hasMore(): boolean {
    return this._hasMore;
  }

  @Output() dateSelected = new EventEmitter<PurchaseItem>();
  @Output() accountSelected = new EventEmitter<PurchaseItem>();
  @Output() nextPage = new EventEmitter<void>();

  @ViewChild(MatRipple) ripple: MatRipple;

  headerClickCount = 0;

  getCurrency(pi: PurchaseItem): Currency {
    return this.currency || pi.currency;
  }

  ngOnInit(): void {
    const totalColumnIndex = this.displayedColumns.indexOf('total');
    const totalTax = this.purchaseItems.reduce<number>((sum, pi) => pi.taxAmount + sum, 0);
    if (totalTax > 0 && this.displayedColumns.indexOf('tax') === -1) {
      if (totalColumnIndex !== -1) {
        this.displayedColumns.splice(totalColumnIndex, 0, 'tax');
      }
    }

    const displaySetupFeeColumn = this.purchaseItems.filter((purchaseItems) => purchaseItems.setupFee > 0);
    if (displaySetupFeeColumn.length > 0 && this.displayedColumns.indexOf('setupFee') === -1) {
      const taxColumnIndex = this.displayedColumns.indexOf('tax');
      // display setup fee to the left of the taxes column if taxes are displayed
      const setupFeeColumnIndex = taxColumnIndex >= 0 ? taxColumnIndex : totalColumnIndex;
      this.displayedColumns.splice(setupFeeColumnIndex, 0, 'setupFee');
    }

    const displayManagementFeeColumn = this.purchaseItems.filter((purchaseItems) => purchaseItems.feeAmount > 0);
    if (displayManagementFeeColumn.length > 0 && this.displayedColumns.indexOf('managementFee') === -1) {
      const taxColumnIndex = this.displayedColumns.indexOf('tax');
      const setupFeeColumnIndex = this.displayedColumns.indexOf('setupFee');
      // display management fee to the left of the taxes column if taxes are displayed
      let managementFeeColumnIndex = taxColumnIndex >= 0 ? taxColumnIndex : totalColumnIndex;

      // display management fee to the right of setup fee column if setup fees are displayed
      if (setupFeeColumnIndex >= 0) {
        managementFeeColumnIndex = setupFeeColumnIndex + 1;
      }
      this.displayedColumns.splice(managementFeeColumnIndex, 0, 'managementFee');
    }
  }

  // Display all columns if header is clicked 5 times
  headerClick(): void {
    this.headerClickCount += 1;
    if (this.headerClickCount >= 5) {
      this.displayedColumns = [
        'name',
        'sku',
        'account',
        this.displayedColumns.includes('day') ? 'day' : null,
        'amount',
        'discount',
        'setupFee',
        'total',
      ].filter((column) => !!column);
      this.headerClickCount = 0;
    }
  }

  // Takes the purchaseDay string of format "20060102" and converts it to a readable date format 'January 2nd 2006'
  purchaseDayToDateString(purchaseDay: string): string {
    let formattedDay = '';
    // wrap in a try block in case our assumption around the input string format is wrong
    try {
      formattedDay = moment(purchaseDay, 'YYYYMMDD').format('MMMM Do, YYYY');
    } catch (e) {
      // Should probably be logging a warning in this condition
      console.warn(e);
    }
    return formattedDay;
  }

  shouldLinkToAccount(purchaseItem: PurchaseItem): boolean {
    return this.showLinkToAccount && purchaseItem.customerId.startsWith('AG-');
  }

  // format the tax info to be displayed, either the amount ($) or rate (%)
  formatAppliedTaxForDisplay(purchaseItem: PurchaseItem, type: 'amount' | 'rate'): string {
    let appliedTaxesCount = 0;
    return purchaseItem.appliedTaxRates.reduce<string>((display, tr) => {
      const amount = formatDisplayPrice(tr.amount, purchaseItem.currency);
      let amountString = `${amount} (${tr.name})`;

      if (type === 'rate') {
        amountString = `${tr.percentage}% ${tr.name}`;
      }

      appliedTaxesCount++;
      if (appliedTaxesCount > 1) {
        amountString = `+ ${amountString}`;
      }
      return `${display} ${amountString}`;
    }, '');
  }

  loadMore(): void {
    // don't trigger the request of the next page if not necessary
    if (this.hasMore) {
      this.nextPage.emit();
    }
  }
}
